import {
  SectionType,
  SectionLectureType,
  LectureType,
  ActivityType,
  OrderedLectureType,
  SectionQuizType,
  CourseActivityType,
  LearningSpaceCourseStatisticsType,
  EnrolledCourseType,
  AgilearnScorm
} from 'types'
import _, { orderBy } from 'lodash'
import { firestore } from 'firebase'
import { getCollectionData } from './FirestoreUtils'
import { getCourse as getLearningSpaceCourse } from './LearningSpaceUtils'
import { IFirestoreMetadata } from 'interfaces'
import { getPaginationStudents } from './LearningSpaceClassUserUtils'
import { splitItems } from './Array'
import { IS_CAPACITOR, IS_ELECTRON } from './Constants'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { agilearnDb } from './AgilearnIndexDB'
import { Zip } from '@awesome-cordova-plugins/zip'
import * as xml2js from 'xml2js'

declare global {
  interface Window {
    APP_PUBLIC: string
    electronAPI?: {
      saveScormZip?: (
        url: string,
        folder: string,
        fileName: string
      ) => Promise<{
        scormFolder: string
        resourceIndexPath: string
      }>
      saveFileFromUrl?: (
        url: string,
        folder: string,
        fileName: string
      ) => Promise<string>
      // Add other Electron API methods here if needed
      checkFileExists?: (filePath: string) => Promise<boolean>
    }
    Zip?: {
      unzip: (
        zipPath: string,
        extractPath: string,
        callback: (resultCode: number) => void
      ) => void
    }
  }
}

/**
 * Get section's id from lecture's sectionRef
 * return null if sectionRef was missed
 * @param lecture
 */
export function getSectionId(lecture: SectionLectureType): string | null {
  if (!lecture.sectionRef) {
    return null
  }
  let arr: string[] = []
  if (typeof lecture.sectionRef === 'object') {
    arr = _.trim(lecture.sectionRef.path, '/').split('/')
  } else if (typeof lecture.sectionRef === 'string') {
    arr = _.trim(lecture.sectionRef, '/').split('/')
  }
  return arr[arr.length - 1]
}

/**
 * Group lectures follow sections keep ordering of both two inputs data
 *
 * @param sections
 * @param lectures
 */
export function groupingLecturesBySection(
  sections: SectionType[],
  lectures: SectionLectureType[]
): Array<SectionType> {
  for (let index = 0; index < sections.length; ++index) {
    let temp: SectionLectureType[] = []
    sections[index].lectures = []

    lectures.forEach((lecture) => {
      if (sections[index].id === getSectionId(lecture)) {
        sections[index].lectures?.push(lecture)
      } else {
        temp.push(lecture)
      }
    })

    lectures = temp //optimize
  }

  return sections
}

export const mergeActivities = (
  lectures: SectionLectureType[],
  quizzes: SectionQuizType[]
) => {
  const activities: CourseActivityType[] = []

  const putTypeField = (type: CourseActivityType['type']) => (act: any) => {
    act.type = type
    return act
  }

  activities.push(...lectures.map(putTypeField('LECTURE')))
  activities.push(...quizzes.map(putTypeField('QUIZ')))
  return activities
}

export const mergeActivitiesAndSortBySection = (
  lectures: SectionLectureType[],
  quizzes: SectionQuizType[],
  sections: SectionType[]
) => {
  const groupSections = groupSectionItems(sections, lectures, quizzes)

  const sortedActivities: CourseActivityType[] = []

  groupSections.forEach((section) => {
    sortedActivities.push(...orderBy(section.activities, ['ordering']))
  })

  return sortedActivities
}

export function getSectionLectureSectionId(
  sectionLecture: CourseActivityType
): string | null {
  if (!sectionLecture.sectionRef) {
    return null
  }
  let arr: string[] = []
  if (typeof sectionLecture.sectionRef === 'object') {
    arr = _.trim(sectionLecture.sectionRef.path, '/').split('/')
  } else if (typeof sectionLecture.sectionRef === 'string') {
    arr = _.trim(sectionLecture.sectionRef, '/').split('/')
  }
  return arr[arr.length - 1]
}

export function groupSectionItems(
  sections: SectionType[],
  lectures: SectionLectureType[],
  quizzes: SectionQuizType[]
): Array<SectionType> {
  let activities = mergeActivities(lectures, quizzes)
  for (let index = 0; index < sections.length; ++index) {
    let groupedActivities: CourseActivityType[] = []
    sections[index].activities = []

    activities.forEach((lecture) => {
      if (sections[index].id === getSectionLectureSectionId(lecture)) {
        sections[index].activities?.push(lecture)
      } else {
        groupedActivities.push(lecture)
      }
    })

    activities = orderBy(groupedActivities, ['ordering']) //optimize
    sections[index].activities = orderBy(sections[index].activities, [
      'ordering'
    ])
  }

  return orderBy(sections, ['ordering'])
}

/**
 * Parse SCORM manifest XML string
 * @param xmlString The XML content as string
 * @returns Parsed manifest object or null if parsing fails
 */
export async function parseManifestXml(xmlString: string): Promise<any> {
  try {
    const parser = new xml2js.Parser({
      explicitArray: false,
      ignoreAttrs: false
    })
    const result = await parser.parseStringPromise(xmlString)
    return result
  } catch (error) {
    console.error('Error parsing XML manifest:', error)
    return null
  }
}

/**
 * Extract useful information from parsed SCORM manifest
 * @param manifestData Parsed manifest object
 * @returns Object containing launch URL, title, and version
 */
function extractScormLaunchInfo(manifestData: any): {
  launchUrl: string
  title?: string
  version?: string
} {
  try {
    const manifest = manifestData?.manifest
    if (!manifest) {
      return { launchUrl: 'index.html' }
    }

    const launchUrl = manifest.resources?.resource?.$?.href || 'index.html'
    const title = manifest.metadata?.title || manifest.$?.identifier
    const version = manifest.$?.version

    return {
      launchUrl,
      title,
      version
    }
  } catch (error) {
    console.error('Error extracting SCORM launch info:', error)
    return { launchUrl: 'index.html' }
  }
}

/**
 * Extract useful information from parsed SCORM manifest
 * @param manifestData Parsed manifest object
 * @returns Object containing launch URL, title, and version
 */
export function getScormLaunchInfo(manifestData: any): {
  launchUrl: string
  title?: string
  version?: string
} {
  return extractScormLaunchInfo(manifestData)
}

/**
 * order lecture activities by activity.ordering field
 * return lecture with ordered activity
 *
 * @param lecture
 */
export function orderActivities(lecture: LectureType): OrderedLectureType {
  const { activities, ...rest } = lecture

  let temp: ActivityType[] = []

  if (activities) {
    temp = Object.values(activities)
    temp.sort((a, b) => {
      const aOrdering = a.ordering ? a.ordering : 0
      const bOrdering = b.ordering ? b.ordering : 0
      return aOrdering - bOrdering
    })
  }

  return { activities: temp, ...rest } as OrderedLectureType
}

export function getLessonTabLabel(type: string): string {
  switch (type) {
    case 'video':
      return 'Video'
    case 'audio':
      return 'Bài Nghe'
    case 'reading':
      return 'Bài Đọc'
    case 'review_question':
      return 'Ôn tập'
    case 'practice':
      return 'Luyện Tập'
    case 'url':
      return 'Liên kết'
    case 'files_list':
      return 'Tài liệu'
    case 'scorm':
      return 'Scorm'
  }

  return ''
}

export const getPublishedSections = async (
  courseRef: firestore.DocumentReference
) => {
  const collection = courseRef.collection('sections')
  const query = collection
    .where('state', '==', 'PUBLISHED')
    .orderBy('ordering', 'asc')

  return getCollectionData<SectionType>(query, { idField: 'id' })
}

export const getPublishedLectures = async (
  courseRef: firestore.DocumentReference
) => {
  const collection = courseRef.collection('lectures')
  const query = collection.where('state', '==', 'PUBLISHED')

  return getCollectionData<SectionLectureType>(query, { idField: 'id' })
}

export const getPublishedQuizzes = async (
  courseRef: firestore.DocumentReference
) => {
  const collection = courseRef.collection('quizzes')
  const query = collection.where('state', '==', 'PUBLISHED')
  return getCollectionData<SectionQuizType>(query, { idField: 'id' })
}

export const getClassCourseCourseActivities = async (
  learningSpaceRef: firestore.DocumentReference,
  classId: string | null,
  courseId: string | null
) => {
  const data: CourseActivityType[] = []
  if (!courseId || !classId) return data

  const lsCourse = await getLearningSpaceCourse(learningSpaceRef, courseId)
  const courseRef = lsCourse?.courseRef

  if (!courseRef) return data

  const [sections, lectures, quizzes] = await Promise.all([
    getPublishedSections(courseRef),
    getPublishedLectures(courseRef),
    getPublishedQuizzes(courseRef)
  ])

  return mergeActivitiesAndSortBySection(lectures, quizzes, sections)
}

export const getStudentCourseStatisticDataInClass = async (
  learningSpaceRef: firestore.DocumentReference,
  classId: string | null,
  courseId: string | null,
  pageToken: null | string,
  limit: number
) => {
  if (limit > 1000) throw new Error('Limit maximum is 1000 documents')

  const data: (LearningSpaceCourseStatisticsType & IFirestoreMetadata)[] = []
  if (!courseId || !classId) return data

  const lsCourse = await getLearningSpaceCourse(learningSpaceRef, courseId)
  const classRef = learningSpaceRef.collection('classes').doc(classId)
  const courseRef = lsCourse?.courseRef

  if (!courseRef) return data

  const activeStudents = await getPaginationStudents(
    classRef,
    pageToken,
    limit,
    ['email', 'asc']
  )
  const userIds = activeStudents.map((u) => u.id)

  const baseQuery = learningSpaceRef
    .collection('courses')
    .doc(courseId)
    .collection('statistics')
    .where('classId', '==', classId)
    .orderBy('email')

  await Promise.all(
    splitItems(userIds, 10).map(async (splitIds) => {
      const result = await getCollectionData<LearningSpaceCourseStatisticsType>(
        baseQuery.where('studentId', 'in', splitIds),
        {
          idField: 'id'
        }
      )
      data.push(...result)
    })
  )

  return data
}

// Helper for ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function convertBlobToBase64(blob: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onload = () => {
      resolve(reader.result)
    }
    reader.readAsDataURL(blob) // base64 data URL
  })
}

// Helper function to parse SCORM manifest XML
async function parseScormManifest(manifestPath: string): Promise<any> {
  try {
    if (IS_CAPACITOR && globalThis.Capacitor?.Plugins?.Filesystem) {
      // Extract the relative path from the full file URI
      // manifestPath example: file:///storage/emulated/0/Android/data/.../files/Agilearn-Contents/...
      let relativePath = manifestPath

      // Remove file:// prefix if present
      if (relativePath.startsWith('file://')) {
        relativePath = relativePath.replace('file://', '')
      }

      // Extract path relative to External directory (Agilearn-Contents/...)
      const agilearnIndex = relativePath.indexOf('Agilearn-Contents/')
      if (agilearnIndex !== -1) {
        relativePath = relativePath.substring(agilearnIndex)
      } else {
        // Fallback: try to extract from common patterns
        relativePath = relativePath.replace(
          /.*\/Agilearn-Contents\//,
          'Agilearn-Contents/'
        )
      }

      console.debug('Reading manifest from relative path:', relativePath)

      // For Capacitor, read the manifest file
      const manifestContent =
        await globalThis.Capacitor.Plugins.Filesystem.readFile({
          path: relativePath,
          directory: Directory.External,
          encoding: 'utf8'
        })

      const parser = new xml2js.Parser({
        explicitArray: false,
        ignoreAttrs: false
      })
      const result = await parser.parseStringPromise(manifestContent.data)
      return result
    }
  } catch (error) {
    console.error('Error parsing SCORM manifest:', error)
    return null
  }

  return null
}

/**
 * Download and save course thumbnail image to local storage
 * Supports both Electron and Capacitor platforms
 */
const downloadAndSaveCourseThumbnailImage = async (
  enrolledId: string,
  thumbnailUrl: string
) => {
  const fileName = 'thumbnail.jpg'
  const path = `Agilearn-Contents/${enrolledId}/${fileName}`

  if (IS_ELECTRON && window.electronAPI?.saveFileFromUrl) {
    const localThumbnailPath = await window.electronAPI.saveFileFromUrl(
      thumbnailUrl,
      path,
      fileName
    )
    return `file://${localThumbnailPath}`
  }

  if (IS_CAPACITOR && globalThis.Capacitor?.Plugins?.Filesystem) {
    await Filesystem.requestPermissions()

    const response = await fetch(thumbnailUrl)
    const blob = await response.blob()
    const arrayBuffer = await blob.arrayBuffer()
    const base64 = arrayBufferToBase64(arrayBuffer)
    const result = await globalThis.Capacitor.Plugins.Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.External,
      recursive: true
    })
    return result
  }

  return thumbnailUrl
}

/**
 * Download SCORM activity specifically for Electron platform
 * Downloads SCORM zip file and extracts it using Electron APIs
 */
const downloadScormActivityForElectronPlatform = async (
  activity: ActivityType,
  enrolledId: string,
  lectureId: string
): Promise<void> => {
  if (!IS_ELECTRON || !window.electronAPI?.saveScormZip) return

  const scormData = activity.data as AgilearnScorm
  if (!scormData?.publicUrl) return

  const url = scormData.publicUrl
  const fileName = `${activity.id}.zip`
  const path = `Agilearn-Contents/${enrolledId}/${lectureId}`

  try {
    const { scormFolder, resourceIndexPath } =
      await window.electronAPI.saveScormZip(url, path, fileName)
    scormData.publicUrl = scormFolder
    scormData.indexPath = resourceIndexPath
  } catch (err) {
    console.error('Failed to save SCORM zip:', err)
  }
}

/**
 * Extract SCORM resource index file path from parsed manifest data
 * Returns the href attribute from the manifest resources
 */
const extractScormResourceIndexPathFromManifest = (
  manifestData: any
): string | null => {
  try {
    return manifestData.manifest.resources.resource.$.href
  } catch (e) {
    return null
  }
}

/**
 * Download and parse SCORM manifest file and update activity data with extracted information
 * Handles manifest parsing, resource extraction, and error fallbacks
 */
const downloadAndParseScormManifestAndUpdateActivityData = async (
  manifestPath: string,
  scormData: AgilearnScorm,
  documentsUri: any
): Promise<void> => {
  try {
    console.debug('SCORM manifest path:', manifestPath)

    const manifestData = await parseScormManifest(manifestPath)
    if (manifestData) {
      console.debug('Parsed SCORM manifest:', manifestData)

      const resourceIndexPath =
        extractScormResourceIndexPathFromManifest(manifestData)
      scormData.publicUrl = documentsUri.uri
      scormData.indexPath = resourceIndexPath || undefined
    }

    console.debug({ scormData })
  } catch (parseError) {
    console.error('Error processing SCORM manifest:', parseError)
    // Fallback to index.html
    scormData.publicUrl = documentsUri.uri
    scormData.indexPath = `${documentsUri.uri}index.html`
  }
}

/**
 * Clean up temporary SCORM zip file after successful extraction
 * Removes the downloaded zip file to save storage space
 */
const cleanupTemporaryScormZipFile = async (zipPath: string): Promise<void> => {
  try {
    await globalThis.Capacitor.Plugins.Filesystem.deleteFile({
      path: zipPath,
      directory: Directory.External
    })
    console.debug('SCORM zip file removed after successful extraction')
  } catch (deleteErr) {
    console.warn('Failed to delete SCORM zip file:', deleteErr)
  }
}

/**
 * Download and extract SCORM zip file and parse manifest using Capacitor platform
 * Handles zip extraction, manifest parsing, and cleanup operations
 */
const downloadExtractScormZipAndParseManifestForCapacitor = (
  zipUri: string,
  extractUri: string,
  manifestPath: string,
  scormData: AgilearnScorm,
  documentsUri: any,
  zipPath: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    Zip.unzip(zipUri, extractUri, async (resultCode: number) => {
      if (resultCode === 0) {
        await downloadAndParseScormManifestAndUpdateActivityData(
          manifestPath,
          scormData,
          documentsUri
        )
        await cleanupTemporaryScormZipFile(zipPath)
        resolve()
      } else {
        reject(new Error(`Unzip failed with code: ${resultCode}`))
      }
    })
  })
}

/**
 * Download SCORM activity specifically for Capacitor platform
 * Downloads SCORM zip, extracts content, and processes manifest
 */
const downloadScormActivityForCapacitorPlatform = async (
  activity: ActivityType,
  enrolledId: string,
  lectureId: string
): Promise<void> => {
  if (!IS_CAPACITOR || !globalThis.Capacitor?.Plugins?.Filesystem) return

  const scormData = activity.data as AgilearnScorm
  if (!scormData?.publicUrl) return

  console.debug(
    'Saving SCORM zip to Capacitor Documents:',
    `Agilearn-Contents/${enrolledId}/${lectureId}`
  )

  const url = scormData.publicUrl
  const fileName = `${activity.id}.zip`
  const zipPath = `Agilearn-Contents/${enrolledId}/${lectureId}/${fileName}`
  const extractPath = `Agilearn-Contents/${enrolledId}/${lectureId}/${activity.id}`

  try {
    // Download and save zip file
    const response = await fetch(url)
    const blob = await response.blob()
    const base64 = await convertBlobToBase64(blob)

    const result = await globalThis.Capacitor.Plugins.Filesystem.writeFile({
      path: zipPath,
      data: base64,
      directory: Directory.External,
      recursive: true
    })

    console.debug('test external dir 2', { result })

    // Get extraction directory URI
    const documentsUri = await globalThis.Capacitor.Plugins.Filesystem.getUri({
      directory: Directory.External,
      path: extractPath
    })

    // Extract zip file and process manifest
    const manifestPath = `${documentsUri.uri}imsmanifest.xml`
    await downloadExtractScormZipAndParseManifestForCapacitor(
      result.uri,
      documentsUri.uri,
      manifestPath,
      scormData,
      documentsUri,
      zipPath
    )

    console.debug(activity.data)
  } catch (err) {
    console.error('Failed to save/extract SCORM zip (Capacitor):', err)
  }
}

/**
 * Download a single activity based on its type and platform capabilities
 * Handles SCORM activities for both Electron and Capacitor platforms
 */
const downloadIndividualActivityByTypeAndPlatform = async (
  activity: ActivityType,
  enrolledId: string,
  lectureId: string
): Promise<ActivityType> => {
  if (activity.type === 'agilearn_scorm') {
    await Promise.all([
      downloadScormActivityForElectronPlatform(activity, enrolledId, lectureId),
      downloadScormActivityForCapacitorPlatform(activity, enrolledId, lectureId)
    ])
  }
  return activity
}

/**
 * Download all activities within a single lecture
 * Downloads and processes SCORM content for each activity
 */
export const downloadAllActivitiesWithinSingleLecture = async (
  lecture: LectureType & { courseRefPath: string },
  enrolledId: string
): Promise<void> => {
  if (!lecture.activities) return

  const processedActivities: ActivityType[] = []
  for (const activity of lecture.activities) {
    const processedActivity = await downloadIndividualActivityByTypeAndPlatform(
      activity,
      enrolledId,
      lecture.id
    )
    processedActivities.push(processedActivity)
  }

  lecture.activities = processedActivities
}

/**
 * Download course content including thumbnails and SCORM activities
 * Processes all lectures and saves data to local storage and IndexedDB
 */
export const downloadCompleteEnrolledCourseWithAllContent = async (
  enrolled: EnrolledCourseType,
  lectures: (LectureType & {
    courseRefPath: string
  })[]
): Promise<void> => {
  // Save course thumbnail if available
  if (enrolled.appearance.thumbnailUrl) {
    enrolled.appearance.thumbnailUrl =
      await downloadAndSaveCourseThumbnailImage(
        enrolled.id,
        enrolled.appearance.thumbnailUrl
      )
  }

  // Request permissions for Capacitor
  if (IS_CAPACITOR) {
    await Filesystem.requestPermissions()
  }

  // Download all lectures and their activities
  await Promise.all(
    lectures.map((lecture) =>
      downloadAllActivitiesWithinSingleLecture(lecture, enrolled.id)
    )
  )

  // Save to IndexedDB
  await Promise.all([
    agilearnDb.lectures.bulkPut(lectures),
    agilearnDb.enrolled.put(enrolled)
  ])
}
